import { useState } from 'react';
import { useLocation } from 'wouter';
import { useMutation } from '@tanstack/react-query';
import { api } from '../lib/api.js';
import { useRequireAuth } from '../lib/auth.js';
import { Button, Card, Logo, MonoLabel } from '../components/primitives/index.js';
import { DropZone } from '../components/composites/index.js';

export function ContestNew() {
  const me = useRequireAuth();
  const [, setLocation] = useLocation();
  const [busy, setBusy] = useState(false);

  const [error, setError] = useState<string | null>(null);

  const onFile = async (file: File) => {
    setError(null);
    if (file.size > 10 * 1024 * 1024) {
      setError('Fișierul depășește 10 MB. Reduce calitatea pozei.');
      return;
    }
    if (!['image/jpeg', 'image/png', 'application/pdf'].includes(file.type)) {
      setError(`Tip neacceptat: ${file.type}. Folosește JPG, PNG sau PDF.`);
      return;
    }

    setBusy(true);
    try {
      // 1. Upload bytes to R2 via the Worker. Worker re-validates MIME + size,
      //    writes to UPLOADS bucket, returns the canonical key.
      const upload = await api.uploadPv(file);

      // 2. Create the contest row, anchored to the uploaded image key.
      //    Status starts as ocr_pending; the /scan route fires /extract on mount.
      const { contest } = await api.createContest({ pv_image_key: upload.key });

      setLocation(`/contest/${contest.id}/scan`);
    } catch (err) {
      setBusy(false);
      const msg = err instanceof Error ? err.message : 'Upload sau creare dosar eșuat.';
      setError(msg);
    }
  };

  if (me.isLoading || !me.data) {
    return (
      <Shell>
        <div className="text-muted text-sm py-12 text-center">Se încarcă…</div>
      </Shell>
    );
  }

  return (
    <Shell>
      <div className="max-w-2xl mx-auto">
        <MonoLabel>Pasul 1 din 4 · upload</MonoLabel>
        <h1 className="text-3xl sm:text-4xl lg:text-display-4 font-medium mt-3 mb-2">Fotografiază procesul-verbal</h1>
        <p className="text-muted leading-relaxed mb-8">
          O singură poză clară, cu tot textul vizibil. AI-ul extrage automat fapta, articolele și
          datele agentului. Nu colectăm CNP-ul.
        </p>

        <Card>
          <DropZone onFile={onFile} disabled={busy} />
          {busy && (
            <div className="mt-4 text-sm text-muted text-center">
              Încărcăm PV-ul și pregătim dosarul…
            </div>
          )}
          {error && !busy && (
            <div className="mt-4 text-sm text-danger text-center">{error}</div>
          )}
        </Card>

        <div className="mt-6 grid grid-cols-1 sm:grid-cols-3 gap-3 sm:gap-4 text-xs text-muted">
          <Tip emoji="💡" title="Lumină naturală">
            Evită fotografii întunecate sau cu flash care reflectă în PV.
          </Tip>
          <Tip emoji="📐" title="Pagina dreaptă">
            Asigură-te că tot PV-ul intră în cadru, fără să fie tăiat.
          </Tip>
          <Tip emoji="🔐" title="Date sigure">
            Originalul se șterge automat după 90 zile. Nu îl trimitem nicăieri.
          </Tip>
        </div>

        <div className="mt-8 flex justify-between items-center">
          <Button
            variant="ghost"
            size="md"
            pill
            icon={false}
            onClick={() => setLocation('/dashboard')}
          >
            ← Înapoi la dashboard
          </Button>
        </div>
      </div>
    </Shell>
  );
}

function Tip({ emoji, title, children }: { emoji: string; title: string; children: React.ReactNode }) {
  return (
    <div className="bg-card border border-line rounded-lg p-4">
      <div className="text-lg mb-1">{emoji}</div>
      <div className="font-medium text-fg-2 mb-1 text-[13px]">{title}</div>
      <div className="leading-snug">{children}</div>
    </div>
  );
}

function Shell({ children }: { children: React.ReactNode }) {
  return (
    <div className="min-h-dvh bg-bg">
      <nav className="px-4 md:px-8 py-4 md:py-5 border-b border-line">
        <Logo />
      </nav>
      <main className="px-4 md:px-6 py-8 md:py-12">{children}</main>
    </div>
  );
}
